package com.islamicmedia.shared.data.api

import kotlinx.coroutines.await
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import org.w3c.fetch.RequestInit
import kotlin.js.Promise
import kotlin.js.json

actual fun createSseFlow(
    url: String,
    body: String,
    authToken: String?,
    extraHeaders: Map<String, String>
): Flow<SseEvent> = callbackFlow {
    val headers = json(
        "Content-Type" to "application/json",
        "Accept" to "text/event-stream"
    )
    if (authToken != null) {
        headers["Authorization"] = "Bearer $authToken"
    }
    extraHeaders.forEach { (key, value) -> headers[key] = value }

    val options = json(
        "method" to "POST",
        "headers" to headers,
        "body" to body
    )

    val response = kotlinx.browser.window.fetch(url, options.unsafeCast<RequestInit>()).await()

    if (!response.ok) {
        close(RuntimeException("SSE connection failed: ${response.status}"))
        return@callbackFlow
    }

    val reader = response.body!!.getReader()
    val decoder = js("new TextDecoder()").unsafeCast<dynamic>()
    var buffer = ""

    try {
        while (true) {
            // reader.read() returns a JS Promise — cast it properly
            val readPromise: Promise<dynamic> = reader.read().unsafeCast<Promise<dynamic>>()
            val result = readPromise.await()
            val done = result.done as Boolean
            if (done) break

            val chunk = decoder.decode(result.value, json("stream" to true)) as String
            buffer += chunk

            // Process complete events (terminated by double newline)
            while (true) {
                val eventEnd = buffer.indexOf("\n\n")
                if (eventEnd == -1) break

                val eventBlock = buffer.substring(0, eventEnd)
                buffer = buffer.substring(eventEnd + 2)

                var eventName = ""
                val dataLines = mutableListOf<String>()
                for (line in eventBlock.split("\n")) {
                    when {
                        line.startsWith("event: ") -> eventName = line.removePrefix("event: ").trim()
                        line.startsWith("data: ") -> dataLines.add(line.removePrefix("data: "))
                    }
                }
                if (eventName.isNotEmpty()) {
                    val eventData = dataLines.joinToString("\n")
                    trySend(SseEvent(eventName, eventData))
                }
            }
        }

        // Process remaining buffer
        if (buffer.isNotBlank()) {
            var eventName = ""
            val dataLines = mutableListOf<String>()
            for (line in buffer.split("\n")) {
                when {
                    line.startsWith("event: ") -> eventName = line.removePrefix("event: ").trim()
                    line.startsWith("data: ") -> dataLines.add(line.removePrefix("data: "))
                }
            }
            if (eventName.isNotEmpty()) {
                trySend(SseEvent(eventName, dataLines.joinToString("\n")))
            }
        }
    } catch (e: dynamic) {
        close(RuntimeException("SSE stream error: $e"))
    }

    close()
    awaitClose {
        try { reader.cancel() } catch (_: dynamic) {}
    }
}
